import { useState } from 'react';

import {
	useDevDocsBylineConnection,
	type DevDocsBylineConnectionAncestor,
} from '@confluence/dev-docs/entry-points/DevDocsBylineConnection.ts';
import { useIsSSROrHydrating } from '@confluence/ssr-utilities/entry-points/useIsSSROrHydrating.ts';

import { getBylinePlaceholderIconCount } from '../ssr/bylinePlaceholderIconCount.ts';
import { useIncomingLinksSsrState } from '../ssr/useIncomingLinksSsrState.ts';
import { useOutboundBylineSsrState } from '../ssr/useOutboundBylineSsrState.ts';
import { useRelatedContentSsrState } from '../ssr/useRelatedContentSsrState.ts';
import type { BylineContentType } from '../utils/contentAri.ts';

export type BylineTileReservation = Readonly<{
	contentId: string;
	contentType: BylineContentType;
	/** Whether this page view started as a hydrated server render. */
	isSsrLoad: boolean;
	/**
	 * Tiles the server painted for this page: one to three, or zero when it painted none. Always zero
	 * after a client-side navigation, where nothing was painted and nothing needs holding.
	 */
	reservedIconCount: number;
}>;

// Why this exists: the page header's byline (`ByLine` in @confluence/content-header) swaps its
// subtree for a skeleton while its own page query loads, and it reloads that query when the page
// changes under the reader (quick reload). That unmounts and remounts this byline during the first
// page load, and a remount must not recount a cache the client has since filled.
//
// Module state lasts one document in the browser, so a map per content id is enough. It is only
// used on a page the server rendered (`__SSR_RENDERED__`) and only before the first navigation;
// after that `isInitialPageLoad` is false and the map is never read again. The server skips it, so
// a count can never carry from one request to another.
const firstPageLoadCounts = new Map<string, number>();

const canRememberFirstPageLoadCounts = (): boolean =>
	// eslint-disable-next-line check-react-ssr-usage/no-react-ssr
	!process.env.REACT_SSR && globalThis.window?.__SSR_RENDERED__ === true;

/**
 * The tile count the server painted, captured on the first render of each mount for a page. It is
 * read above the Suspense boundary, so it is taken on the first render even when the loaded byline
 * suspends, and passed to both placeholders and the loaded byline, so they all see one value.
 * Reading the samples again later is wrong: the client's own Appears in and Related content queries
 * fill the same cache entries after paint, so a recount grows the stack when the server undercounted.
 * A remount during the first page load reuses the first count. CCINT-9543
 */
export const useBylineTileReservation = ({
	contentId,
	contentType,
	connectionAncestors,
}: {
	contentId: string;
	contentType: BylineContentType;
	connectionAncestors?: readonly DevDocsBylineConnectionAncestor[] | null;
}): BylineTileReservation => {
	const outboundState = useOutboundBylineSsrState(contentId);
	const incomingLinksState = useIncomingLinksSsrState(contentId, contentType);
	const relatedContentState = useRelatedContentSsrState(contentId, contentType);
	const devDocsConnection = useDevDocsBylineConnection(connectionAncestors);
	const isSSROrHydrating = useIsSSROrHydrating();
	// Also true when a server-rendered page is rendered fresh on the client instead of hydrated: the
	// server still painted the tiles, and the preloaded samples are still in the cache (CCINT-8752).
	// False after a client-side navigation. A client-only first load has no samples, so it counts 0.
	const isInitialPageLoad = useIsSSROrHydrating(true);

	const countFirstPageLoad = (): number => {
		const canRemember = canRememberFirstPageLoadCounts();
		const count =
			(canRemember ? firstPageLoadCounts.get(contentId) : undefined) ??
			getBylinePlaceholderIconCount({
				outboundState,
				incomingLinksState,
				relatedContentState,
				devDocsConnection,
			});
		if (canRemember) {
			firstPageLoadCounts.set(contentId, count);
		}
		return count;
	};
	const capture = (): BylineTileReservation => ({
		contentId,
		contentType,
		isSsrLoad: isSSROrHydrating,
		reservedIconCount: isInitialPageLoad ? countFirstPageLoad() : 0,
	});
	const [reservation, setReservation] = useState(capture);
	// Keyed on the page alone: the tiles were painted for this page, so a content type that settles
	// later (page versus blog post) must not drop them. The error boundary above remounts this per
	// content id, so this branch only runs if a parent reuses the component across pages.
	if (reservation.contentId !== contentId) {
		const next = capture();
		setReservation(next);
		return next;
	}
	return reservation;
};
